import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  forwardRef,
  MouseEventHandler,
  PropsWithChildren,
} from "react";
import { twMerge } from "tailwind-merge";
import { classNames } from "../../utils/class-names";
import {
  actionTriggerClassMap,
  globalActionTriggerClassNames,
} from "../../styles/action-trigger-styles";

export const buttonVariants = [
  "primary",
  "secondary",
  "tertiary",
  "quaternary",
  "action",
  "outline",
  "ghost",
  "pulse",
  "success",
  "warning",
  "link",
] as const;

export type ButtonVariant = (typeof buttonVariants)[number];

export const buttonSizes = ["small", "normal", "big"] as const;

export type ButtonSize = (typeof buttonSizes)[number];

type ButtonType = ButtonHTMLAttributes<HTMLButtonElement>["type"];

type ButtonProps = {
  variant: ButtonVariant;
  type?: ButtonType;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  size?: ButtonSize;
  className?: string;
  ["data-cy"]?: string;
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

type Classes = Record<"all" | ButtonSize | "enabled" | "disabled", string>;

const variantMap: Record<ButtonVariant, Classes> = {
  primary: {
    all: "",
    small: "px-4 py-[5px] text-sm",
    normal: "px-5 py-2.5 text-base font-medium",
    big: "px-6 py-3.5 text-xl",
    enabled: "bg-blue-500 hover:bg-blue-400 active:bg-blue-600 text-white",
    disabled: "bg-shale-750 text-shale-400",
  },
  secondary: {
    all: "",
    small: "px-4 py-[5px] text-sm",
    normal: "px-5 py-2.5 text-base font-medium",
    big: "px-6 py-3.5 text-xl",
    enabled: "bg-shale-500 hover:bg-shale-400 active:bg-shale-600 text-white",
    disabled: "bg-shale-750 text-shale-400",
  },
  tertiary: {
    all: "",
    small: "px-4 py-[5px] text-sm",
    normal: "px-5 py-2.5 text-base font-medium",
    big: "px-6 py-3.5 text-xl",
    enabled: "bg-shale-800 hover:bg-shale-750 text-white active:bg-shale-800",
    disabled: "bg-shale-800 text-shale-400",
  },
  quaternary: {
    all: "",
    small: "px-4 py-[5px] text-sm",
    normal: "px-5 py-2.5 text-base font-medium",
    big: "px-6 py-3.5 text-xl",
    enabled: "bg-shale-600 hover:bg-shale-500 active:bg-shale-700 text-white",
    disabled: "bg-shale-750 text-shale-400",
  },
  action: {
    all: globalActionTriggerClassNames,
    small: actionTriggerClassMap.small.all,
    normal: actionTriggerClassMap.normal.all,
    // unsupported for now
    big: "",
    enabled: actionTriggerClassMap.normal.unselected,
    disabled: actionTriggerClassMap.normal.disabled,
  },
  outline: {
    all: "",
    small: "",
    normal: "px-5 py-2.5 text-base font-medium",
    big: "",
    enabled: "text-blue-300 hover:text-blue-200 border border-shale-700",
    disabled: "text-shale-400",
  },
  ghost: {
    all: "",
    small: "px-3.5 text-sm",
    normal: "px-5 text-sm font-medium",
    big: "px-[1.375rem] text-xl",
    enabled: "text-blue-300 hover:text-blue-200",
    disabled: "text-shale-400",
  },
  pulse: {
    all: "",
    small: "px-4 py-[5px] text-sm",
    normal: "px-4 py-2.5 text-base",
    big: "px-6 py-3.5 text-xl",
    enabled: "",
    disabled: "bg-shale-750 text-shale-400 animate-pulse",
  },
  success: {
    all: "",
    small: "px-4 py-[5px] text-sm",
    normal: "px-4 py-2.5 text-base",
    big: "px-6 py-3.5 text-xl",
    enabled: "bg-green-500 hover:bg-green-400 active:bg-green-600 text-black",
    disabled: "bg-shale-750 text-shale-400",
  },
  warning: {
    all: "",
    small: "px-4 py-[5px] text-sm",
    normal: "px-5 py-2.5 text-base font-medium",
    big: "px-6 py-3.5 text-xl",
    enabled:
      "bg-amber-400 hover:bg-amber-300 active:bg-amber-500 text-black/90",
    disabled: "bg-shale-750 text-shale-400",
  },
  link: {
    all: "font-medium",
    small: "text-sm",
    normal: "text-base",
    big: "text-xl",
    enabled: "text-blue-300 hover:text-blue-200",
    disabled: "text-shale-400",
  },
};

export const getButtonClassNames = (
  variant: ButtonVariant,
  size: ButtonSize,
  disabled = false
): string =>
  classNames(
    "rounded-lg font-medium text-center hover:transition hover:ease-in hover:duration-100",
    variantMap[variant].all,
    variantMap[variant][size],
    disabled ? variantMap[variant].disabled : variantMap[variant].enabled
  );

export const Button = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonProps>
>(
  (
    {
      variant,
      // TODO: can eventually make 'primary' the default once the design overhaul is complete
      // variant = "primary",
      type = "button",
      size = "normal",
      disabled = false,
      onClick,
      className = "",
      children,
      "data-cy": dataCy,
      ...rest
    },
    ref
  ) => (
    <button
      className={twMerge(
        getButtonClassNames(variant, size, disabled),
        className
      )}
      type={type}
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled}
      disabled={disabled}
      ref={ref}
      data-cy={dataCy}
      {...rest}
    >
      {children}
    </button>
  )
);
